import type { QueryParameters } from '@/hooks'

import { unwrapResult } from '@reduxjs/toolkit'
import { useCallback } from 'react'

import { updateAccountDataToken, useEnvironmentURLs } from '@/atoms'
import { login } from '@/features/actions/login'
import { useNotification } from '@/hooks'
import { useNavigate } from '@/routing'
import { recoverPasswordService } from '@/services'
import { useAppDispatch } from '@/store'

import { useActionsTexts } from './useActionsTexts'

export function useLoginActions(query: QueryParameters) {
  const toast = useNotification()
  const texts = useActionsTexts()
  const dispatch = useAppDispatch()
  const navigate = useNavigate<uui.routing.MainSections>(true)
  const { serverUrl, aiRoutingAssistantUrl } = useEnvironmentURLs()

  const territoryId = query?.territoryId ?? undefined
  const simulationId = query?.simulationId ?? undefined

  const onLogin = useCallback(
    async data => {
      const { username, password /* rememberMe */ } = data

      try {
        const loginRequest = await dispatch(
          login({
            username,
            password,
            serverUrl,
            territoryId,
            simulationId,
            aiRoutingAssistantUrl,
          }),
        )

        if (login.fulfilled.match(loginRequest)) {
          const result = unwrapResult(loginRequest)

          // Now we're forcing rememberMe to TRUE, please replace with following line
          // when remember me will be managed correctly.
          //
          // updateAccountDataToken(username, result.authtoken, rememberMe)
          updateAccountDataToken(username, result.authtoken, true)
        } else {
          const message = texts.loginError(loginRequest.payload)
          toast.error(message)
        }
      } catch (e) {
        // un-expected error
        navigate('error')
      }
    },
    [dispatch, navigate, toast, serverUrl, texts, territoryId, simulationId, aiRoutingAssistantUrl],
  )

  const forgotPassword = useCallback(
    async (data: { username: string }) => {
      const { username } = data

      try {
        await recoverPasswordService({ username, serverUrl })
        toast.success(texts.forgotPasswordSuccess)
      } catch (e) {
        toast.error(texts.forgotPasswordError)
      }
    },
    [texts, toast, serverUrl],
  )

  return { onLogin, forgotPassword }
}
